<template>
  <div
    v-if="isActive"
    :class="['acToast', 'toast-transition', `-${type}`, `-${location}`, `-position-${textPosition}`]"
  >
    <div v-if="canClose" class="acToast_close" @click="close()">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_2088_9251)">
          <path d="M12.0007 10.5867L16.2437 6.34372C16.6342 5.95325 17.2673 5.95325 17.6577 6.34372V6.34372C18.0482 6.73418 18.0482 7.36725 17.6577 7.75772L13.4147 12.0007L17.6577 16.2437C18.0482 16.6342 18.0482 17.2673 17.6577 17.6577V17.6577C17.2673 18.0482 16.6342 18.0482 16.2437 17.6577L12.0007 13.4147L7.75772 17.6577C7.36725 18.0482 6.73418 18.0482 6.34372 17.6577V17.6577C5.95325 17.2673 5.95325 16.6342 6.34372 16.2437L10.5867 12.0007L6.34372 7.75772C5.95325 7.36725 5.95325 6.73418 6.34372 6.34372V6.34372C6.73418 5.95325 7.36725 5.95325 7.75772 6.34372L12.0007 10.5867Z" fill="#FFFFFF"/>
        </g>
        <defs>
          <clipPath id="clip0_2088_9251">
            <rect width="24" height="24" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </div>

    <div class="acToast_wrapper">
      <slot>
        <div class="acToast_icon"><slot name="icon">{{ icon }}</slot></div>

        <div class="acToast_content">
          <div class="acToast_title"><slot name="title">{{ title }}</slot></div>

          <div class="acToast_text"><slot name="text">{{ text }}</slot></div>
        </div>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue'

export default defineComponent({
  name: 'AcToast',
  props: {
    modelValue: {
      type: Boolean,
      default: true
    },
    canClose: {
      type: Boolean,
      default: true
    },
    type: {
      type: String as PropType<'info' | 'warn' | 'error'>,
      default: 'info'
    },
    location: {
      type: String as PropType<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center' | 'center'>,
      default: 'bottom-right'
    },
    timeout: {
      type: Number,
      default: 0
    },
    icon: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: null
    },
    text: {
      type: String,
      default: null
    },
    textPosition: {
      type: String as PropType<'left' | 'right' | 'center'>,
      default: 'left'
    }
  },
  emit: ['close'],
  setup (props, ctx) {
    let activeTimeout = -1

    const isActive = ref(props.modelValue)

    function close () {
      window.clearTimeout(activeTimeout)
      ctx.emit('close')
      isActive.value = false
    }

    function startTimeout () {
      window.clearTimeout(activeTimeout)

      if (!props.timeout || !isActive.value) return

      activeTimeout = setTimeout(() => {
        close()
      }, props.timeout * 1000)
    }

    startTimeout()

    return {
      isActive,
      close
    }
  }
})
</script>

<style lang="scss" scoped>
@import '@vars';
@import '@web/assets/scss/textContsnts.scss';

$decelerated-easing: cubic-bezier(0.0, 0, 0.2, 1);

.acToast {
  position: fixed;
  background: var(--ac-color-deep-liliac);
  color: var(--ac-color-white);
  border-radius: 12px;
  max-width: 100%;
  z-index: 9999;

  padding-top: 14px;
  padding-right: 12px;
  padding-bottom: 13px;
  padding-left: 10px;

  // Type
  &.-info {
    background: var(--ac-color-deep-liliac);
    color: var(--ac-color-white);
  }
  &.-warn {
    background: var(--ac-color-orange);
    color: var(--ac-color-white);
  }
  &.-error {
    background: var(--ac-color-red);
    color: var(--ac-color-white);
  }

  // Location
  &.-top-left,
  &.-top-right {
    top: 10px;
    left: 6px;
    right: 6px;
  }
  &.-bottom-left,
  &.-bottom-right {
    bottom: 10px;
    left: 6px;
    right: 6px;
  }
  &.-center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  &.-top-center{
    left: 50%;
    top: 66px;
    transform: translate(-50%, 0);
  }
  &.-bottom-center{
    left: 50%;
    bottom: 28px;
    transform: translate(-50%, 0);
  }

  @media (min-width: $bp-tablet) {
    &.-top-left {
      top: 28px;
      left: 30px;
      right: unset;
    }
    &.-top-right {
      top: 28px;
      right: 85px;
      left: unset;
    }
    &.-bottom-left {
      bottom: 28px;
      left: 30px;
      right: unset;
    }
    &.-bottom-right {
      bottom: 28px;
      right: 85px;
      left: unset;
    }
    &.-top-center{
      top: 28px;
    }
    &.-bottom-center{
      bottom: 28px;
    }
  }

  &_close {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 2;
    cursor: pointer;

    svg path {
      fill: var(--ac-color-white);
    }
  }

  &_wrapper {
    position: relative;
    z-index: 1;
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-start;
    justify-content: flex-start;
  }

  &.-position-left &_wrapper {
    justify-content: flex-start;
  }
  &.-position-right &_wrapper {
    justify-content: flex-end;
  }
  &.-position-center &_wrapper {
    justify-content: center;
  }

  &_icon {
    margin-right: 8px;

    &:empty {
      display: none;
    }
  }

  &_title {
    font-weight: 500;
    font-size: 16px;
    line-height: 18px;
    color: currentColor;
    @extend .text-base-medium;

    &:empty {
      display: none;
    }
  }

  &_text {
    font-weight: 400;
    font-size: 13px;
    line-height: 17px;
    color: currentColor;
    margin-bottom: 8px;

    &:empty {
      display: none;
    }
  }
}

.toast-transition {
  &-enter-active,
  &-leave-active {
    transition-duration: .15s;
    transition-timing-function: $decelerated-easing;
  }

  &-enter-active {
    transition-property: opacity, transform;
  }

  &-enter-from {
    opacity: 0;
    transform: scale(.8);
  }

  &-leave-active {
    transition-property: opacity
  }

  &-leave-to {
    opacity: 0
  }
}
</style>
